import React, { useEffect, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import {
  pdfToArrayBuffer,
  validatePdfFile,
  getPdfPageCount,
  splitPdfIntoTemplatePages
} from '../../utils/pdfUtils';
import { loadTemplates, saveTemplate, removeTemplate, loadDefaultTemplates, shouldLoadDefaults, markDefaultsAsLoaded } from '../../utils/templateStorage';
import { Template } from '../../types';

export function TemplateLibrary() {
  const { state, dispatch } = useApp();
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [expandedBundleId, setExpandedBundleId] = useState<string | null>(null);

  useEffect(() => {
    const initializeTemplates = async () => {
      let templates = loadTemplates();

      // Load default templates on first run
      if (shouldLoadDefaults() && templates.length === 0) {
        console.log('Loading default templates...');
        const defaultTemplates = await loadDefaultTemplates();

        // Save default templates to localStorage
        for (const template of defaultTemplates) {
          saveTemplate(template);
        }

        templates = loadTemplates();
        markDefaultsAsLoaded();
        console.log('Default templates loaded successfully');
      }

      dispatch({ type: 'SET_TEMPLATES', payload: templates });
    };

    initializeTemplates();
  }, [dispatch]);

  const onTemplateDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    for (const file of acceptedFiles) {
      const validationError = validatePdfFile(file);

      if (validationError) {
        dispatch({ type: 'SET_ERROR', payload: validationError });
        continue;
      }

      try {
        const pdfData = await pdfToArrayBuffer(file);
        const pageCount = await getPdfPageCount(pdfData);

        // Check if it's a multi-page PDF (bundle)
        if (pageCount >= 2) {
          // Create bundle
          const { pages } = await splitPdfIntoTemplatePages(pdfData);

          const template: Template = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name.replace('.pdf', ''),
            pdfData, // Keep original for backward compatibility
            isBundle: true,
            pages,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          saveTemplate(template);
          dispatch({ type: 'ADD_TEMPLATE', payload: template });

          // Show info message if more than 3 pages
          if (pageCount > 3) {
            console.warn(
              `Bundle "${template.name}" had ${pageCount} pages. Only the first 3 (voorblad, vervolgblad, eindblad) were used.`
            );
          }
        } else {
          // Single page template
          const template: Template = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name.replace('.pdf', ''),
            pdfData,
            isBundle: false,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          saveTemplate(template);
          dispatch({ type: 'ADD_TEMPLATE', payload: template });
        }
      } catch (error) {
        dispatch({
          type: 'SET_ERROR',
          payload: error instanceof Error ? error.message : 'Fout bij uploaden template'
        });
      }
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onTemplateDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const handleTemplateSelect = (template: Template) => {
    dispatch({ type: 'SET_TEMPLATE', payload: template });
  };

  const handleTemplateDelete = (templateId: string) => {
    if (window.confirm('Weet je zeker dat je deze template wilt verwijderen?')) {
      removeTemplate(templateId);
      dispatch({ type: 'REMOVE_TEMPLATE', payload: templateId });
    }
  };

  const handleStartEditing = (template: Template) => {
    setEditingTemplateId(template.id);
    setEditingName(template.name);
  };

  const handleSaveEdit = (templateId: string) => {
    if (editingName.trim()) {
      const template = state.templates.find(t => t.id === templateId);
      if (template) {
        const updatedTemplate = {
          ...template,
          name: editingName.trim(),
          updatedAt: new Date()
        };
        saveTemplate(updatedTemplate);
        dispatch({ type: 'UPDATE_TEMPLATE', payload: updatedTemplate });
      }
    }
    setEditingTemplateId(null);
    setEditingName('');
  };

  const toggleBundleExpand = (templateId: string) => {
    setExpandedBundleId(expandedBundleId === templateId ? null : templateId);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'voorblad':
        return 'Voorblad';
      case 'vervolgblad':
        return 'Vervolgblad';
      case 'eindblad':
        return 'Eindblad';
      default:
        return role;
    }
  };

  const handleCancelEdit = () => {
    setEditingTemplateId(null);
    setEditingName('');
  };

  const handlePreviewTemplatePage = (templateName: string, page: any) => {
    // Create a temporary template for preview
    const previewTemplate: Template = {
      id: 'preview-' + page.id,
      name: `${templateName} - ${getRoleLabel(page.role)}`,
      pdfData: page.pdfData,
      isBundle: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Set this as the selected template for preview
    dispatch({ type: 'SET_TEMPLATE', payload: previewTemplate });
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Template Bibliotheek</h2>
      
      <div className="mb-6 flex justify-end">
        <div
          {...getRootProps()}
          className={`
            inline-flex items-center px-3 py-2 border-2 border-dashed rounded-md text-sm cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
              : 'border-gray-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-600 dark:text-white'
            }
          `}
        >
          <input {...getInputProps()} />
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {isDragActive ? 'Uploaden...' : 'Template toevoegen'}
        </div>
      </div>

      {state.templates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p>Nog geen templates beschikbaar</p>
          <p className="text-sm">Upload je eerste briefpapier template</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {state.templates.map((template) => (
            <div
              key={template.id}
              className={`
                border rounded-lg p-4 cursor-pointer transition-all hover:shadow-lg
                bg-white dark:bg-black
                ${state.selectedTemplate?.id === template.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 shadow-md'
                  : 'border-gray-200 dark:border-blue-600 hover:border-gray-300 dark:hover:border-blue-400'
                }
              `}
              onClick={() => editingTemplateId !== template.id && handleTemplateSelect(template)}
            >
              <div className="flex justify-between items-start mb-3 gap-2">
                {editingTemplateId === template.id ? (
                  <div className="flex-1 mr-2">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(template.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex space-x-1 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveEdit(template.id);
                        }}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="Opslaan"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEdit();
                        }}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Annuleren"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 dark:text-white text-sm break-words">
                      {template.name}
                    </h3>
                    {template.isBundle && template.pages && (
                      <span className="inline-block mt-1.5 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded whitespace-nowrap">
                        Bundle ({template.pages.length})
                      </span>
                    )}
                  </div>
                )}

                <div className="flex space-x-0.5 flex-shrink-0">
                  {editingTemplateId !== template.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditing(template);
                      }}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title="Naam bewerken"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTemplateDelete(template.id);
                    }}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Template verwijderen"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 5a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 012 0v3a1 1 0 11-2 0V9zm4 0a1 1 0 012 0v3a1 1 0 11-2 0V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              
              {template.category && (
                <span className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded mb-2">
                  {template.category}
                </span>
              )}

              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                <p>{template.createdAt.toLocaleDateString()}</p>
              </div>

              {/* Bundle expand section */}
              {template.isBundle && template.pages && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBundleExpand(template.id);
                    }}
                    className="flex items-center text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 w-full"
                  >
                    <svg
                      className={`w-3 h-3 mr-1 transition-transform ${
                        expandedBundleId === template.id ? 'rotate-90' : ''
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {expandedBundleId === template.id ? 'Verberg' : 'Bekijk bladen'}
                  </button>

                  {expandedBundleId === template.id && (
                    <div className="mt-1.5 space-y-1">
                      {template.pages.map((page, index) => (
                        <button
                          key={page.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewTemplatePage(template.name, page);
                          }}
                          className="w-full flex items-center justify-between py-1.5 px-2 bg-gray-50 dark:bg-gray-900 hover:bg-blue-50 dark:hover:bg-blue-950 rounded text-xs transition-colors cursor-pointer border border-transparent hover:border-blue-300 dark:hover:border-blue-700"
                          title="Klik om dit blad te bekijken in de preview"
                        >
                          <span className="text-gray-600 dark:text-gray-400">
                            {index + 1}.
                          </span>
                          <span className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1">
                            {getRoleLabel(page.role)}
                            <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}